import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Link, Navigate, NavLink, useParams } from 'react-router'
import { IconBack, IconChevronRight } from '../components/icons'
import { useMediaQuery } from '../components/useMediaQuery'
import { cn } from '../lib/cn'
import { ConnectorsPage } from './ConnectorsPage'
import { Memory, Skills } from './KnowledgePage'
import { KnowledgeBase } from './KnowledgeBasePage'
import { TeamsPage } from './TeamsPage'
import { UsagePage } from './UsagePage'
import { AuditLogCard, Invites, Providers, ServerSettingsCard, Stats, Users } from './admin/AdminPage'
import { useTranslation } from 'react-i18next'
import { api } from '../api/http'
import { rpc } from '../api/rpc'
import { session, useSession } from '../api/session'
import type { ModelGroup, ModelList, User } from '../api/types'
import { useModels } from '../components/useModels'
import { Button } from '../components/ui/Button'
import { Badge, Card } from '../components/ui/Card'
import { Cell, Row, RowMenu, Table, Td, Th } from '../components/ui/DataTable'
import { Field, Input, Segmented } from '../components/ui/Field'
import { Select } from '../components/ui/Select'
import { Modal } from '../components/ui/Modal'
import { useToast } from '../components/ui/Toast'
import { confirmDialog } from '../components/ui/Confirm'
import { formatBytes, formatDateTime } from '../lib/format'
import { useThemePref, type ThemePref } from '../lib/theme'

const TIMEZONES = ['Asia/Ho_Chi_Minh', 'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Los_Angeles', 'UTC']

/**
 * The panel head every settings section shares (docs/web-ui.md §6): eyebrow "Group / Section",
 * the h1, a one-line lede, a status line and the section's primary actions on the right.
 */
function PanelHead({ eyebrow, title, lede, status, actions }: {
  eyebrow: ReactNode; title: ReactNode; lede?: ReactNode; status?: ReactNode; actions?: ReactNode
}) {
  return (
    <header className="mb-5 flex items-end justify-between gap-6 border-b border-line pb-4 max-sm:flex-col max-sm:items-start max-sm:gap-3">
      <div className="min-w-0">
        <div className="font-mono text-[11px] uppercase tracking-[0.08em] text-ink-500">{eyebrow}</div>
        <h1 className="mt-2 font-display text-[30px] font-bold tracking-tight">{title}</h1>
        {lede && <p className="mt-2 max-w-[70ch] text-sm text-ink-400">{lede}</p>}
        {status && <p className="mt-2 text-[13.5px] text-ink-400">{status}</p>}
      </div>
      {actions && <div className="flex items-end gap-2 pb-0.5">{actions}</div>}
    </header>
  )
}

function Profile({ user }: { user: User }) {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [theme, setTheme] = useThemePref()
  const [name, setName] = useState(user.name)
  const [locale, setLocale] = useState(user.locale)
  const [timezone, setTimezone] = useState(user.timezone)
  const [busy, setBusy] = useState(false)
  const [storage, setStorage] = useState<{ usedBytes: number; quotaBytes: number } | null>(null)
  const zones = TIMEZONES.includes(user.timezone) ? TIMEZONES : [user.timezone, ...TIMEZONES]
  // Read-only: the storage card only ever issues a GET, so the Files page is unaffected.
  useEffect(() => { void api.get<{ usedBytes: number; quotaBytes: number }>('/api/files').then(setStorage).catch(() => undefined) }, [])
  const roleLabel = t(user.role === 'admin' ? 'admin.users.admin' : 'admin.users.member')
  const pct = storage && storage.quotaBytes > 0 ? Math.min(100, (storage.usedBytes / storage.quotaBytes) * 100) : 0
  return (
    <div className="space-y-6">
      <PanelHead
        eyebrow={`${t('settings.group.account')} / ${t('settings.nav.profile')}`}
        title={t('settings.profile')}
        status={<>
          {t('admin.users.role')} <b>{roleLabel}</b>
          <span className="px-1.5 text-ink-500">·</span>
          {formatDateTime(user.createdAt, i18n.language)}
          <span className="px-1.5 text-ink-500">·</span>
          {t('admin.users.lastLogin')} <b>{user.lastLoginAt ? formatDateTime(user.lastLoginAt, i18n.language) : t('common.never')}</b>
        </>}
      />
      <Card title={t('settings.identity')}>
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={async e => {
            e.preventDefault()
            setBusy(true)
            try {
              const u = await api.patch<User>('/api/me', { name, locale, timezone })
              session.setUser(u)
              void i18n.changeLanguage(u.locale)
              toast(t('common.saved'), 'success')
            } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
          }}
        >
          <Field label={t('auth.name')}>{id => <Input id={id} value={name} onChange={e => setName(e.target.value)} />}</Field>
          <Field label={t('auth.email')} hint={t('settings.emailLocked')}>
            {id => <Input id={id} value={user.email} readOnly className="bg-cream-100 text-ink-500" />}
          </Field>
          <Field label={t('settings.language')}>{id => (
            <Select
              id={id} value={locale} onChange={v => setLocale(v)}
              options={[
                { value: 'vi', label: 'Tiếng Việt' },
                { value: 'en', label: 'English' },
              ]}
            />
          )}</Field>
          <Field label={t('settings.timezone')} hint={t('settings.timezoneHint')}>{id => (
            <Select
              id={id} value={timezone} onChange={v => setTimezone(v)}
              options={zones.map(z => ({ value: z, label: z }))}
            />
          )}</Field>
          <div className="sm:col-span-2">
            <Field label={t('theme.label')}>
              {() => (
                <div>
                  <Segmented<ThemePref>
                    value={theme}
                    onChange={setTheme}
                    label={t('theme.label')}
                    options={[
                      { value: 'light', label: t('theme.light') },
                      { value: 'dark', label: t('theme.dark') },
                      { value: 'system', label: t('theme.system') },
                    ]}
                  />
                </div>
              )}
            </Field>
          </div>
          <div className="sm:col-span-2"><Button type="submit" loading={busy}>{t('common.save')}</Button></div>
        </form>
      </Card>
      <Card title={t('settings.storage')}>
        <div className="h-2 rounded-full bg-cream-200">
          <div className="h-full rounded-full bg-mint-500" style={{ width: `${pct}%` }} />
        </div>
        {storage && (
          <p className="mt-2.5 font-mono text-sm text-ink-300">
            {formatBytes(storage.usedBytes)} / {formatBytes(storage.quotaBytes)}
          </p>
        )}
        <p className="mt-1 text-xs text-ink-500">{t('settings.storageHint')}</p>
      </Card>
    </div>
  )
}

function Password() {
  const { t } = useTranslation()
  const toast = useToast()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)

  const mismatch = confirm.length > 0 && next !== confirm

  return (
    <Card title={t('settings.password')} subtitle={t('settings.passwordSubtitle')}>
      <form
        className="max-w-md space-y-4"
        onSubmit={async e => {
          e.preventDefault()
          if (next !== confirm) {
            toast(t('auth.mismatch'), 'error')
            return
          }
          setBusy(true)
          try {
            await api.post('/api/me/password', { currentPassword: current, newPassword: next })
            setCurrent('')
            setNext('')
            setConfirm('')
            toast(t('settings.passwordChanged'), 'success')
          } catch (err) {
            toast((err as Error).message, 'error')
          } finally {
            setBusy(false)
          }
        }}
      >
        <Field label={t('settings.currentPassword')}>
          {id => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={e => setCurrent(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('settings.newPassword')} hint={t('auth.passwordHint')}>
          {id => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={next}
              onChange={e => setNext(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('auth.confirmPassword')}
          error={mismatch ? t('auth.mismatch') : undefined}
        >
          {id => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              minLength={10}
              required
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
            />
          )}
        </Field>
        <div className="pt-1">
          <Button type="submit" loading={busy} disabled={mismatch || !current || !next || !confirm}>
            {t('settings.changePassword')}
          </Button>
        </div>
      </form>
    </Card>
  )
}

interface DeviceSession {
  id: string; userAgent: string | null; ip: string | null; createdAt: number; expiresAt: number; current: boolean
}

/** A Chrome extension signed in with this account (docs/extension.md §7). */
interface ExtensionDevice {
  id: string; name: string; createdAt: number; lastUsedAt: number | null
}

function parseDevice(ua: string | null): string {
  if (!ua) return ''
  let browser = 'Browser'
  if (/Edg\//i.test(ua)) browser = 'Edge'
  else if (/Chrome\//i.test(ua)) browser = 'Chrome'
  else if (/Safari\//i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari'
  else if (/Firefox\//i.test(ua)) browser = 'Firefox'
  else if (/Opera|OPR\//i.test(ua)) browser = 'Opera'

  let os = ''
  if (/iPhone/i.test(ua)) os = 'iPhone'
  else if (/iPad/i.test(ua)) os = 'iPad'
  else if (/Android/i.test(ua)) os = 'Android'
  else if (/Windows/i.test(ua)) os = 'Windows'
  else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macOS'
  else if (/Linux/i.test(ua)) os = 'Linux'

  return os ? `${browser} (${os})` : browser
}

function Sessions() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [items, setItems] = useState<DeviceSession[]>([])
  const load = useCallback(
    () => api.get<{ items: DeviceSession[] }>('/api/me/sessions').then(r => setItems(r.items)).catch(() => undefined),
    []
  )
  useEffect(() => { void load() }, [load])
  const others = items.filter(s => !s.current).length
  return (
    <Card
      title={t('settings.sessions')}
      subtitle={t('settings.sessionsHint')}
      flush
      actions={
        others > 0 ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              if (!(await confirmDialog(t('settings.confirmRevokeOthers')))) return
              try {
                await api.post('/api/me/sessions/revoke-others', {})
                await load()
              } catch (err) {
                toast((err as Error).message, 'error')
              }
            }}
          >
            {t('settings.revokeOthers')}
          </Button>
        ) : undefined
      }
    >
      {items.length === 0 ? (
        <p className="px-5 py-5 text-sm text-ink-500">{t('settings.noSessions')}</p>
      ) : (
        <Table minWidth={760}>
          <thead>
            <tr>
              <Th rail />
              <Th>{t('settings.device')}</Th>
              <Th>{t('settings.ipAddress')}</Th>
              <Th>{t('settings.signedIn')}</Th>
              <Th>{t('settings.expires')}</Th>
              <Th><span className="sr-only">{t('common.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => {
              const devName = parseDevice(s.userAgent)
              return (
                <Row key={s.id} rail={s.current ? 'ok' : 'idle'}>
                  <Td>
                    <Cell
                      title={
                        <span className="flex items-center gap-2">
                          <span>{devName || t('settings.unknownDevice')}</span>
                          {s.current && <Badge tone="accent">{t('settings.thisDevice')}</Badge>}
                        </span>
                      }
                      sub={
                        s.userAgent ? (
                          <span className="block max-w-sm truncate font-mono text-xs text-ink-500" title={s.userAgent}>
                            {s.userAgent}
                          </span>
                        ) : undefined
                      }
                    />
                  </Td>
                  <Td className="font-mono text-xs text-ink-400">{s.ip ?? '—'}</Td>
                  <Td className="text-ink-400">{formatDateTime(s.createdAt, i18n.language)}</Td>
                  <Td className="text-ink-400">{formatDateTime(s.expiresAt, i18n.language)}</Td>
                  <Td>
                    {s.current ? null : (
                      <RowMenu
                        items={[{
                          label: t('settings.revokeSession'),
                          danger: true,
                          onSelect: async () => {
                            if (!(await confirmDialog(t('settings.confirmRevokeSession')))) return
                            try {
                              await api.del('/api/me/sessions/' + s.id)
                              await load()
                            } catch (err) {
                              toast((err as Error).message, 'error')
                            }
                          }
                        }]}
                      />
                    )}
                  </Td>
                </Row>
              )
            })}
          </tbody>
        </Table>
      )}
    </Card>
  )
}

function Extensions() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [items, setItems] = useState<ExtensionDevice[]>([])
  const load = useCallback(
    () => api.get<{ items: ExtensionDevice[] }>('/api/me/extension-tokens').then(r => setItems(r.items)).catch(() => undefined),
    []
  )
  useEffect(() => { void load() }, [load])
  return (
    <Card title={t('settings.extensions')} subtitle={t('settings.extensionsHint')} flush>
      {items.length === 0 ? (
        <p className="px-5 py-5 text-sm text-ink-500">{t('settings.noExtensions')}</p>
      ) : (
        <Table minWidth={640}>
          <thead>
            <tr>
              <Th rail />
              <Th>{t('settings.extensionDevice')}</Th>
              <Th>{t('settings.extensionConnected')}</Th>
              <Th>{t('settings.extensionLastUsed')}</Th>
              <Th><span className="sr-only">{t('common.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {items.map(d => (
              <Row key={d.id} rail="ok">
                <Td>
                  <Cell
                    title={d.name}
                    sub={<span className="block font-mono text-xs text-ink-500">{d.id.slice(0, 8)}</span>}
                  />
                </Td>
                <Td className="text-ink-400">{formatDateTime(d.createdAt, i18n.language)}</Td>
                <Td className="text-ink-400">{d.lastUsedAt ? formatDateTime(d.lastUsedAt, i18n.language) : '—'}</Td>
                <Td>
                  <RowMenu
                    items={[{
                      label: t('settings.revokeExtension'),
                      danger: true,
                      onSelect: async () => {
                        if (!(await confirmDialog(t('settings.confirmRevokeExtension')))) return
                        try {
                          await api.del('/api/me/extension-tokens/' + d.id)
                          await load()
                        } catch (err) {
                          toast((err as Error).message, 'error')
                        }
                      }
                    }]}
                  />
                </Td>
              </Row>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  )
}

function Security() {
  const { t } = useTranslation()
  return (
    <div className="space-y-6">
      <PanelHead
        eyebrow={`${t('settings.group.account')} / ${t('settings.nav.security')}`}
        title={t('settings.nav.security')}
        lede={t('settings.securityLede')}
      />
      <Password />
      <Sessions />
      <Extensions />
    </div>
  )
}

interface KeyForm { id: string; providerType: string; apiKey: string; baseUrl: string; models: string }

const EMPTY_KEY: KeyForm = { id: '', providerType: 'openai-compatible', apiKey: '', baseUrl: '', models: '' }

function PersonalProviders() {
  const { t } = useTranslation()
  const toast = useToast()
  const { models, setModels } = useModels()
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState<KeyForm>(EMPTY_KEY)
  const personal = models?.groups.filter(g => g.scope === 'personal') ?? []
  const shared = models?.groups.filter(g => g.scope === 'shared') ?? []

  const openAdd = () => {
    setEditingId(null)
    setForm(EMPTY_KEY)
    setOpen(true)
  }

  const openEdit = (g: ModelGroup) => {
    setEditingId(g.providerId)
    setForm({
      id: g.providerId,
      providerType: g.providerType ?? 'openai-compatible',
      apiKey: '',
      baseUrl: g.baseUrl ?? '',
      models: g.models.join(', ')
    })
    setOpen(true)
  }

  const removeKey = async (g: ModelGroup) => {
    if (!(await confirmDialog(t('settings.confirmRemoveKey', { name: g.name })))) return
    try {
      setModels(await rpc.call<ModelList>('models.personal.disconnect', { providerId: g.providerId }))
      if (open && editingId === g.providerId) {
        setOpen(false)
        setEditingId(null)
      }
      toast(t('common.saved'), 'success')
    } catch (err) {
      toast((err as Error).message, 'error')
    }
  }

  const submit = async () => {
    if (!editingId && !form.apiKey.trim()) {
      toast(t('settings.apiKey') + ' is required', 'error')
      return
    }
    setBusy(true)
    try {
      const payload: {
        id: string
        providerType?: string
        apiKey?: string
        baseUrl?: string
        models?: string[]
      } = {
        id: form.id.trim(),
        providerType: form.providerType
      }
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim()
      if (form.baseUrl.trim()) payload.baseUrl = form.baseUrl.trim()
      if (form.models.trim()) {
        payload.models = form.models.split(',').map(s => s.trim()).filter(Boolean)
      }
      setModels(await rpc.call<ModelList>('models.personal.connect', payload))
      setForm(EMPTY_KEY)
      setEditingId(null)
      setOpen(false)
      toast(t('common.saved'), 'success')
    } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  return (
    <div className="space-y-6">
      <PanelHead
        eyebrow={`${t('settings.group.account')} / ${t('settings.nav.models')}`}
        title={t('settings.nav.models')}
        lede={t('settings.modelsHint')}
        status={<>
          <b>{shared.length}</b> {t('models.shared')}
          <span className="px-1.5 text-ink-500">·</span>
          <b>{personal.length}</b> {t('models.personal')}
        </>}
        actions={<Button onClick={openAdd}>{t('settings.addKey')}</Button>}
      />

      <Card title={t('settings.sharedModels')} flush>
        {shared.length === 0 ? (
          <p className="px-5 py-4 text-sm text-ink-500">{t('common.none')}</p>
        ) : (
          <Table minWidth={640} footer={<span>{t('settings.modelsHint')}</span>}>
            <thead>
              <tr>
                <Th rail />
                <Th>{t('settings.nav.models')}</Th>
                <Th>{t('settings.modelIds')}</Th>
                <Th>{t('usage.bySource')}</Th>
              </tr>
            </thead>
            <tbody>
              {shared.map(g => (
                <Row key={g.providerId}>
                  <Td><Cell title={g.name} sub={g.providerId} /></Td>
                  <Td className="text-ink-400">{g.models.join(', ') || '—'}</Td>
                  <Td><Badge tone="accent">{t('models.shared')}</Badge></Td>
                </Row>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title={t('settings.personalKeys')} flush>
        {personal.length === 0 ? (
          <div className="space-y-1 px-5 py-4 text-sm">
            <p className="text-ink-400">{t('settings.personalKeysHint')}</p>
            <p className="text-ink-500">{t('common.none')}</p>
          </div>
        ) : (
          <Table minWidth={760} footer={<span>{t('settings.personalKeysHint')}</span>}>
            <thead>
              <tr>
                <Th rail />
                <Th>{t('settings.keyName')}</Th>
                <Th>{t('settings.modelIds')}</Th>
                <Th>{t('settings.keyStatus')}</Th>
                <Th><span className="sr-only">{t('common.actions')}</span></Th>
              </tr>
            </thead>
            <tbody>
              {personal.map(g => {
                // The server reports no per-key health, so an empty model list is the rejection signal.
                const verified = g.models.length > 0
                return (
                  <Row key={g.providerId} rail={verified ? 'ok' : 'error'} onClick={() => openEdit(g)}>
                    <Td><Cell title={g.name} sub={g.providerId} /></Td>
                    <Td className="text-ink-400">{g.models.join(', ') || '—'}</Td>
                    <Td>
                      <Badge tone={verified ? 'success' : 'danger'}>
                        {verified ? t('settings.keyVerified') : t('settings.keyRejected')}
                      </Badge>
                    </Td>
                    <Td>
                      <RowMenu items={[
                        {
                          label: t('common.edit'),
                          onSelect: () => openEdit(g)
                        },
                        {
                          label: t('common.remove'),
                          danger: true,
                          onSelect: () => void removeKey(g)
                        }
                      ]} />
                    </Td>
                  </Row>
                )
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={open}
        title={editingId ? t('settings.editKey') : t('settings.addKey')}
        description={editingId ? t('admin.providers.keepKey') : t('settings.personalKeysHint')}
        onClose={() => { setOpen(false); setEditingId(null) }}
        footer={
          <>
            {editingId && (
              <Button
                variant="danger"
                className="sm:mr-auto"
                onClick={() => {
                  const g = personal.find(p => p.providerId === editingId)
                  if (g) void removeKey(g)
                }}
              >
                {t('common.remove')}
              </Button>
            )}
            <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null) }}>{t('common.cancel')}</Button>
            <Button loading={busy} onClick={() => void submit()}>{editingId ? t('common.save') : t('settings.checkAndSave')}</Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('settings.providerId')} hint={editingId ? undefined : t('settings.providerIdHint')}>
            {id => (
              <Input
                id={id}
                required
                disabled={Boolean(editingId)}
                pattern="[a-z0-9][a-z0-9-]*"
                value={form.id}
                onChange={e => setForm({ ...form, id: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('settings.providerType')}>{id => (
            <Select
              id={id} value={form.providerType}
              onChange={v => setForm({ ...form, providerType: v })}
              options={[
                { value: 'anthropic', label: 'Anthropic' },
                { value: 'google', label: 'Google Gemini' },
                { value: 'openai-compatible', label: 'OpenAI-compatible' },
              ]}
            />
          )}</Field>
          <Field label={t('settings.apiKey')} hint={editingId ? t('admin.providers.keepKey') : undefined}>
            {id => (
              <Input
                id={id}
                type="password"
                autoComplete="off"
                required={!editingId}
                placeholder={editingId ? '••••••••' : undefined}
                value={form.apiKey}
                onChange={e => setForm({ ...form, apiKey: e.target.value })}
              />
            )}
          </Field>
          <Field label={t('settings.baseUrl')} hint={t('settings.baseUrlHint')}>{id => <Input id={id} type="url" value={form.baseUrl} onChange={e => setForm({ ...form, baseUrl: e.target.value })} />}</Field>
          <div className="sm:col-span-2">
            <Field label={t('settings.modelIds')} hint={t('settings.modelIdsHint')}>{id => <Input id={id} value={form.models} onChange={e => setForm({ ...form, models: e.target.value })} />}</Field>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function Sandbox() {
  const { t } = useTranslation()
  const toast = useToast()
  const [status, setStatus] = useState<{ enabled: boolean; running: boolean } | null>(null)
  const load = () => void rpc.call<{ enabled: boolean; running: boolean }>('sandbox.status').then(setStatus).catch(() => undefined)
  useEffect(load, [])
  const head = (actions?: ReactNode) => (
    <PanelHead
      eyebrow={`${t('settings.group.workspace')} / ${t('settings.nav.sandbox')}`}
      title={t('settings.nav.sandbox')}
      lede={t('settings.sandboxHint')}
      actions={actions}
    />
  )
  if (status && !status.enabled) return (
    <div className="space-y-6">
      {head()}
      <Card><p className="text-sm text-ink-400">{t('settings.sandboxDisabled')}</p></Card>
    </div>
  )
  if (!status) return null
  return (
    <div className="space-y-6">
      {head(
        <Button variant="secondary" onClick={async () => {
          if (!(await confirmDialog(t('settings.sandboxResetConfirm'), { danger: false }))) return
          try { await rpc.call('sandbox.reset'); toast(t('settings.sandboxResetDone'), 'success'); load() } catch (err) { toast((err as Error).message, 'error') }
        }}>{t('settings.sandboxReset')}</Button>
      )}
      <Card title={t('settings.sandbox')}>
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={status.running ? 'success' : 'neutral'}>{status.running ? t('settings.sandboxRunning') : t('settings.sandboxStopped')}</Badge>
          <span className="text-sm text-ink-500">{t('settings.sandboxIdle')}</span>
        </div>
      </Card>
    </div>
  )
}

type SectionId =
  | 'profile' | 'security' | 'models' | 'usage'
  | 'connectors' | 'teams' | 'knowledge' | 'memory' | 'skills' | 'sandbox'
  | 'users' | 'invites' | 'providers' | 'server' | 'audit' | 'status'

const GROUPS: { id: 'account' | 'workspace' | 'admin'; admin?: boolean; items: SectionId[] }[] = [
  { id: 'account', items: ['profile', 'security', 'models', 'usage'] },
  { id: 'workspace', items: ['connectors', 'teams', 'knowledge', 'memory', 'skills', 'sandbox'] },
  { id: 'admin', admin: true, items: ['users', 'invites', 'providers', 'server', 'audit', 'status'] }
]
const ADMIN_ONLY = new Set<SectionId>(GROUPS[2].items)

function Section({ id, user }: { id: SectionId; user: User }) {
  switch (id) {
    case 'profile': return <Profile user={user} />
    case 'security': return <Security />
    case 'models': return <PersonalProviders />
    case 'usage': return <UsagePage />
    case 'connectors': return <ConnectorsPage />
    case 'teams': return <TeamsPage />
    case 'knowledge': return <KnowledgeBase />
    case 'memory': return <Memory />
    case 'skills': return <Skills />
    case 'sandbox': return <Sandbox />
    case 'users': return <Users />
    case 'invites': return <Invites />
    case 'providers': return <Providers />
    case 'server': return <ServerSettingsCard />
    case 'audit': return <AuditLogCard />
    case 'status': return <Stats />
  }
}

function SettingsNav({ admin }: { admin: boolean }) {
  const { t } = useTranslation()
  return (
    <nav aria-label={t('settings.title')} className="flex flex-col gap-5">
      {GROUPS.filter(g => !g.admin || admin).map(g => (
        <div key={g.id} className="flex flex-col gap-px">
          <div className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-500">{t(`settings.group.${g.id}`)}</div>
          {g.items.map(id => (
            <NavLink key={id} to={`/settings/${id}`}
              className={({ isActive }) => cn('rounded-lg px-2.5 py-2 text-sm', isActive ? 'bg-ginger-100 font-semibold text-ginger-600' : 'text-ink-300 hover:bg-cream-200 hover:text-ink-100')}>
              {t(`settings.nav.${id}`)}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  )
}

/** Mobile: the settings index is a grouped list; each row opens its section. */
function SettingsIndexList({ admin }: { admin: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-5 px-4 py-5">
      <h1 className="px-1 font-display text-[30px] font-bold tracking-tight">{t('settings.title')}</h1>
      {GROUPS.filter(g => !g.admin || admin).map(g => (
        <div key={g.id} className="space-y-1.5">
          <div className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-500">{t(`settings.group.${g.id}`)}</div>
          <div className="overflow-hidden rounded-2xl border border-line bg-cream-50">
            {g.items.map((id, i) => (
              <Link key={id} to={`/settings/${id}`} className={cn('flex min-h-[52px] items-center gap-3 px-4 text-[15px] text-ink-100', i > 0 && 'border-t border-cream-200')}>
                <span className="flex-1">{t(`settings.nav.${id}`)}</span>
                <IconChevronRight width={16} height={16} className="text-ink-500" />
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function SettingsPage() {
  const { t } = useTranslation()
  const { user } = useSession()
  const { section } = useParams()
  const wide = useMediaQuery('(min-width: 768px)')
  if (!user) return null
  const admin = user.role === 'admin'
  const id = section as SectionId | undefined
  if (id && (!GROUPS.some(g => g.items.includes(id)) || (ADMIN_ONLY.has(id) && !admin))) return <Navigate to="/settings" replace />
  if (!id && wide) return <Navigate to="/settings/profile" replace />
  if (!id) return <SettingsIndexList admin={admin} />
  return (
    <div className="flex min-h-full">
      <aside className="hidden w-[250px] shrink-0 border-r border-line bg-sidebar px-3.5 py-7 md:block">
        <div className="mb-5 px-2.5 font-display text-[22px] font-bold tracking-tight">{t('settings.title')}</div>
        <SettingsNav admin={admin} />
      </aside>
      <div className="min-w-0 flex-1 px-4 py-5 sm:px-8 md:px-10 md:py-9">
        <Link to="/settings" className="mb-3 inline-flex h-10 items-center gap-1 rounded-lg pr-2 text-sm font-medium text-ink-300 md:hidden">
          <IconBack width={18} height={18} />{t('settings.title')}
        </Link>
        <div className="max-w-[880px]"><Section id={id} user={user} /></div>
      </div>
    </div>
  )
}
